import {
  CLASSIFICATION_SCOPE_SENTENCE_LABELS,
  CLASSIFICATION_SCOPES,
} from '@shared/constants/classification-scopes';
import { COMPANY_SIZE_BAND_LABELS, COMPANY_SIZE_BANDS } from '@shared/constants/company-size-bands';
import type { PreviewProfileTypeBlockDraftRequest, SaveProfileTypeBlockRequest } from '@shared/schemas';

import type {
  BlockDraft,
  ClassificationScope,
  CompanySizeBand,
  DraftCondition,
  DraftField,
  ProfileTypeBlock,
} from './profile-type.model';

/**
 * The block editor's business rules: building a draft, reading one back off a saved
 * type, editing its conditions and fields, deciding whether it can be saved or
 * previewed, and turning it into the request payloads. Pure; the editor renders
 * this, it does not decide any of it.
 *
 * A block is the PO's grid line for line: conditions that must ALL match (AND), each
 * met by ANY one of its fields (OR), each field by any one of its keywords.
 */

let uidSeq = 0;
/** A screen-only key for a new condition (see `DraftCondition.uid`). */
const nextUid = () => `c${++uidSeq}`;

const condition = (fields: DraftField[]): DraftCondition => ({
  uid: nextUid(),
  kind: 'keywords',
  fields,
  companySizes: [],
});

const sizeCondition = (bands: CompanySizeBand[]): DraftCondition => ({
  uid: nextUid(),
  kind: 'company_size',
  fields: [],
  companySizes: bands,
});

/** Whether a condition checks the company size (#724) rather than keywords. */
export const isCompanySize = (c: { kind?: string }) => c.kind === 'company_size';

/**
 * A blank draft: one condition with a job title field to fill; the field almost
 * every block starts from. Automatic tagging is OFF until the admin turns it on: a
 * block being written should not tag every profile enriched meanwhile.
 */
export function emptyDraft(): BlockDraft {
  return {
    name: '',
    isNew: true,
    category: '',
    autoClassify: false,
    conditions: [condition([{ scope: 'job_title', keywords: [] }])],
  };
}

/** A saved type as an editable draft. A type with no block yet gets one empty condition. */
export function draftFromBlock(block: ProfileTypeBlock): BlockDraft {
  return {
    name: block.name,
    isNew: false,
    category: block.category ?? '',
    autoClassify: block.auto_classify,
    conditions:
      block.conditions.length > 0
        ? block.conditions.map((c) =>
            isCompanySize(c)
              ? sizeCondition([...(c.company_sizes ?? [])])
              : condition(c.fields.map((f) => ({ ...f, keywords: [...f.keywords] }))),
          )
        : emptyDraft().conditions,
  };
}

// ── Editing ──

export function addCondition(draft: BlockDraft): BlockDraft {
  return { ...draft, conditions: [...draft.conditions, condition([{ scope: 'job_title', keywords: [] }])] };
}

/**
 * Add the company size condition (#724), with nothing ticked yet. A block has one at
 * most: two ANDed would only keep the bands they share.
 */
export function addCompanySizeCondition(draft: BlockDraft): BlockDraft {
  if (!canAddCompanySizeCondition(draft)) return draft;
  return { ...draft, conditions: [...draft.conditions, sizeCondition([])] };
}

/** Whether the block can still take a company size condition: it has none yet. */
export function canAddCompanySizeCondition(draft: BlockDraft): boolean {
  return !draft.conditions.some(isCompanySize);
}

/** Tick or untick one band; the bands stay in their natural order, smallest first. */
export function toggleCompanySize(draft: BlockDraft, conditionIndex: number, band: CompanySizeBand): BlockDraft {
  return updateCondition(draft, conditionIndex, (c) => {
    const picked = new Set(c.companySizes);
    if (picked.has(band)) picked.delete(band);
    else picked.add(band);
    return { ...c, companySizes: COMPANY_SIZE_BANDS.filter((b) => picked.has(b)) };
  });
}

export function removeCondition(draft: BlockDraft, index: number): BlockDraft {
  return { ...draft, conditions: draft.conditions.filter((_, i) => i !== index) };
}

const updateCondition = (draft: BlockDraft, index: number, fn: (c: DraftCondition) => DraftCondition) => ({
  ...draft,
  conditions: draft.conditions.map((c, i) => (i === index ? fn(c) : c)),
});

/** Add a field (an OR alternative) to a condition. */
export function addField(draft: BlockDraft, conditionIndex: number, scope: ClassificationScope): BlockDraft {
  return updateCondition(draft, conditionIndex, (c) => ({
    ...c,
    fields: [...c.fields, { scope, keywords: [] }],
  }));
}

export function removeField(draft: BlockDraft, conditionIndex: number, fieldIndex: number): BlockDraft {
  return updateCondition(draft, conditionIndex, (c) => ({
    ...c,
    fields: c.fields.filter((_, i) => i !== fieldIndex),
  }));
}

export function setKeywords(
  draft: BlockDraft,
  conditionIndex: number,
  fieldIndex: number,
  keywords: string[],
): BlockDraft {
  return updateCondition(draft, conditionIndex, (c) => ({
    ...c,
    fields: c.fields.map((f, i) => (i === fieldIndex ? { ...f, keywords } : f)),
  }));
}

/**
 * Swap a field for another; the field picker at the head of each field. Its keywords
 * stay: an admin who put "CIO" under job title and meant headline should not have to
 * type them again.
 */
export function setFieldScope(
  draft: BlockDraft,
  conditionIndex: number,
  fieldIndex: number,
  scope: ClassificationScope,
): BlockDraft {
  return updateCondition(draft, conditionIndex, (c) => ({
    ...c,
    fields: c.fields.map((f, i) => (i === fieldIndex ? { ...f, scope } : f)),
  }));
}

/** What a field's picker offers: its own field, then those the condition does not use yet. */
export function scopeChoices(condition: DraftCondition, fieldIndex: number): ClassificationScope[] {
  const own = condition.fields[fieldIndex].scope;
  return CLASSIFICATION_SCOPES.filter((s) => s === own || !condition.fields.some((f) => f.scope === s));
}

/**
 * The fields a condition can still offer. One field appears once per condition:
 * two lists on the same field are one list split in two, and the backend refuses it.
 */
export function unusedScopes(condition: DraftCondition): ClassificationScope[] {
  const used = new Set(condition.fields.map((f) => f.scope));
  return CLASSIFICATION_SCOPES.filter((s) => !used.has(s));
}

// ── Reading ──

/** The fields of a condition carrying at least one keyword; its actual alternatives. */
export function usableFields(condition: DraftCondition): DraftField[] {
  return condition.fields.filter((f) => f.keywords.length > 0);
}

export interface DraftProblems {
  name?: string;
  category?: string;
  /** By condition index: what is wrong with it. */
  conditions: Record<number, string>;
  /** Not tied to a single field. */
  form?: string;
}

/**
 * What is wrong with the draft, as admin-readable sentences.
 *
 * The same rules the backend enforces (`validateBlockDraft` there), on purpose: the
 * backend's copy is the barrier, this one is what stops a save from bouncing back as
 * an anonymous 400 next to a block the admin can no longer see the fault in.
 */
export function draftProblems(draft: BlockDraft): DraftProblems {
  const problems: DraftProblems = { conditions: {} };

  if (draft.name.trim().length === 0) {
    problems.name = 'Required.';
  } else if (/[,;]/.test(draft.name)) {
    // It is the tag written into `profiles.types` and a primary key in the type
    // whitelist: a comma would read as two types wherever a list is joined.
    problems.name = 'No comma or semicolon; this is stored as one tag.';
  }

  if (draft.category === '') problems.category = 'Choose HNWI or Institutionals.';

  if (draft.conditions.length === 0) problems.form = 'Add at least one condition.';
  else if (draft.conditions.every(isCompanySize)) {
    // The size narrows a block, it cannot be one: alone it would tag every company
    // of that size. The backend refuses it with the same sentence.
    problems.form = 'Add a keyword condition: a company size alone does not say what kind of profile this is.';
  }

  draft.conditions.forEach((c, i) => {
    if (isCompanySize(c)) {
      if (c.companySizes.length === 0) problems.conditions[i] = 'Tick at least one company size, or remove this condition.';
      return;
    }
    if (usableFields(c).length === 0) {
      problems.conditions[i] = 'Add keywords to one of its fields, or remove this condition.';
    }
  });

  return problems;
}

export function isDraftSubmittable(draft: BlockDraft): boolean {
  const p = draftProblems(draft);
  return !p.name && !p.category && !p.form && Object.keys(p.conditions).length === 0;
}

/**
 * Whether the draft can be previewed; weaker than saving on purpose: an admin pasting
 * keywords to see who comes back should not have to file the type first. It does
 * need a name (the tag the count is about) and a block that can match something.
 */
export function isDraftPreviewable(draft: BlockDraft): boolean {
  const p = draftProblems(draft);
  return !p.name && !p.form && Object.keys(p.conditions).length === 0;
}

const toConditions = (draft: BlockDraft) =>
  draft.conditions.map((c) => ({
    kind: c.kind,
    fields: c.fields.map((f) => ({ scope: f.scope, keywords: f.keywords })),
    company_sizes: [...c.companySizes],
  }));

/**
 * The draft as the save payload. Fields left empty still travel (the backend drops
 * them) so what is stored is exactly what the admin left on screen minus nothing.
 */
export function draftToPayload(draft: BlockDraft): SaveProfileTypeBlockRequest {
  if (draft.category === '') throw new Error('draftToPayload: category not chosen');
  return { category: draft.category, auto_classify: draft.autoClassify, conditions: toConditions(draft) };
}

/** The draft as a preview payload; the block's predicate and the tag, nothing else. */
export function draftToPreviewPayload(draft: BlockDraft): PreviewProfileTypeBlockDraftRequest {
  return { name: draft.name.trim(), conditions: toConditions(draft) };
}

/** How many keywords a readback line names before it summarises the rest. */
const NAMED_KEYWORDS = 3;

function keywordPhrase(keywords: string[]): string {
  if (keywords.length <= NAMED_KEYWORDS) {
    return keywords.length === 1
      ? keywords[0]
      : `${keywords.slice(0, -1).join(', ')} or ${keywords[keywords.length - 1]}`;
  }
  return `${keywords.slice(0, NAMED_KEYWORDS).join(', ')}… (${keywords.length} keywords)`;
}

function bandPhrase(bands: CompanySizeBand[]): string {
  const labels = bands.map((b) => COMPANY_SIZE_BAND_LABELS[b]);
  return labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

/**
 * The block in plain words, one line per condition: "Job title or headline contains
 * President, CIO, Founder… (15 keywords)". The panel joins the lines with AND.
 *
 * The keywords are named because they are what the admin is checking; "its job title
 * matches" says nothing a reviewer can disagree with. Past three, the rest is counted:
 * the line is a readback, not a second copy of the field. The fields of one condition
 * share one keyword list in the sentence (duplicates once), which is how the PO's grid
 * writes them. Conditions with no keyword yet are left out; they ask nothing yet. The
 * company size reads "Company size is 201-500 or 10,001+ employees" (#724).
 */
export function plainWords(draft: BlockDraft): string[] {
  return draft.conditions.flatMap((c) => {
    if (isCompanySize(c)) {
      // Every band is named: there are nine at most, and the bands ARE the condition.
      return c.companySizes.length === 0 ? [] : [`Company size is ${bandPhrase(c.companySizes)} employees`];
    }
    const fields = usableFields(c);
    if (fields.length === 0) return [];
    const names = fields.map((f) => CLASSIFICATION_SCOPE_SENTENCE_LABELS[f.scope]).join(' or ');
    const keywords = [...new Set(fields.flatMap((f) => f.keywords))];
    return [`${names.charAt(0).toUpperCase()}${names.slice(1)} contains ${keywordPhrase(keywords)}`];
  });
}
